import { Badge, TableProps } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { HistoryWithdraw } from "../../constants/Withdraw";
import useCustomToast from "../../lib/useCustomToast";
import { usePagination } from "../../lib/usePagination";
import { useSorting } from "../../lib/useSorting";
import formatNumber, {
  formatDate,
  getAxiosInstance,
} from "../../utils/helperFunction";
import { Empty } from "../card/Empty";
import { CardSkeleton } from "../skeleton/CardSkeleton";
import { CustomTable } from "../table/CustomTable";

interface Props extends TableProps {
  filterSearch?: string;
  pageType: number;
}

export const HistoryWithdrawTable = ({
  filterSearch,
  pageType,
  ...rest
}: Props) => {
  const [data, setData] = useState<HistoryWithdraw[]>([]);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [formattedData, setFormattedData] = useState<any[]>([]);
  const toast = useCustomToast();

  // Initialize pagination and sorting
  const { currentPage, limitPagination, handlePageChange, handleLimitChange } =
    usePagination();
  const { sortedData, sortConfig, handleSort } = useSorting(formattedData); // Pass formatted data to sorting hook

  useEffect(() => {
    const axiosInstance = getAxiosInstance(pageType);
    let userType: string = "buyer";

    if (pageType === 2) {
      userType = "reseller";
    } else if (pageType === 3) {
      userType = "supplier";
    }

    axiosInstance
      .get(`/v1/${userType}/point`)
      .then((res) => {
        setData(res.data.data.withdraw);
      })
      .catch((err) => {
        toast({
          description: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [pageType]);

  useEffect(() => {
    if (data.length > 0) {
      const newFormattedData = data.map((item, i) => ({
        index: i + 1,
        point: item.points,
        formattedPoint: formatNumber(item.points),
        status: (
          <Badge
            w={"fit-content"}
            borderRadius={"md"}
            colorScheme={
              item.status === 1 ? "yellow" : item.status === 2 ? "green" : "red"
            }
          >
            {item.status_name}
          </Badge>
        ),
        date: item.created_at, // Keep the original date for sorting
        formattedDate: formatDate(item.created_at.toString()), // Keep the formatted string for display
      }));
      setFormattedData(newFormattedData);
    }
  }, [data]);

  const columnHeader = [
    {
      id: "index",
      name: "No.",
      sortable: false, // Index is not sortable
      onClick: undefined,
    },
    {
      id: "point",
      name: "Point",
      sortable: true,
      onClick: () => handleSort("point"),
    },
    {
      id: "status",
      name: "Status",
      sortable: false,
      onClick: undefined,
    },
    {
      id: "date",
      name: "Tanggal",
      sortable: true,
      onClick: () => handleSort("date", true),
    },
  ];

  if (!loaded) {
    return <CardSkeleton row={5} column={10} />;
  } else if (loaded && formattedData.length < 1) {
    return <Empty />;
  }

  // Calculate paginated data
  const startIndex = (currentPage - 1) * limitPagination;
  const paginatedData = sortedData
    .slice(startIndex, startIndex + limitPagination)
    .map((item) => ({
      ...item,
      point: item.formattedPoint,
      date: item.formattedDate, // Ensure date is a formatted string
    }));

  return (
    <CustomTable
      columnHeader={columnHeader}
      data={paginatedData}
      totalPages={Math.ceil(formattedData.length / limitPagination)}
      totalItems={formattedData.length}
      onPageChange={handlePageChange}
      onLimitChange={handleLimitChange}
      {...rest}
    />
  );
};
